import { useCallback, useEffect, useState } from 'react'
import type { AuthUser } from '@crm/shared'
import { apiClient } from '../../shared/api/client'
import { useAuthStore } from '../auth/authStore'

const KEY = 'planningSelection'

function readFromUser(user: AuthUser | null): Set<string> {
  const gs = user?.gridSettings as Record<string, unknown> | null | undefined
  const sel = gs?.[KEY]
  if (Array.isArray(sel)) return new Set(sel.filter((x): x is string => typeof x === 'string'))
  return new Set<string>()
}

/**
 * Хранит выбор менеджеров для графика план/факт в users.gridSettings.planningSelection.
 *
 * Чтение: из текущего user в authStore (хранится на сервере, подтягивается через
 * /auth/me). Запись: PATCH /auth/me { gridSettings: { ...prev, planningSelection } }
 * + локальный апдейт authStore, чтобы виджет на дашборде увидел новый список без
 * дополнительного запроса. Ошибка PATCH — silent (UX не страдает, при F5
 * подтянется верное значение с сервера).
 */
export function usePlanningSelection() {
  const user = useAuthStore((s) => s.user)
  const [selection, setSelectionState] = useState<Set<string>>(() => readFromUser(user))

  // Синхронизация при обновлении user в store (например после fetchMe).
  useEffect(() => {
    setSelectionState(readFromUser(user))
  }, [user])

  const save = useCallback(async (next: Set<string>) => {
    const current = useAuthStore.getState().user
    if (!current) return
    const prevGs = (current.gridSettings ?? {}) as Record<string, unknown>
    const nextGs = { ...prevGs, [KEY]: Array.from(next) }
    // Оптимистичный апдейт стора — чтобы PlanFactChart мгновенно увидел изменение.
    useAuthStore.setState({ user: { ...current, gridSettings: nextGs } })
    try {
      await apiClient.patch('/auth/me', { gridSettings: nextGs })
    } catch {
      /* ignore — состояние в БД синхронизируется при следующем fetchMe */
    }
  }, [])

  const toggle = useCallback(
    (id: string) => {
      setSelectionState((prev) => {
        const next = new Set(prev)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        void save(next)
        return next
      })
    },
    [save],
  )

  const setAll = useCallback(
    (ids: string[]) => {
      const next = new Set(ids)
      setSelectionState(next)
      void save(next)
    },
    [save],
  )

  const clear = useCallback(() => {
    setSelectionState(new Set())
    void save(new Set())
  }, [save])

  return { selection, toggle, setAll, clear }
}
